// Dashboard widget — smart detection of repetitive workflow execution patterns
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Zap, Check, X } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";

export default function InsightsWidget() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    api.get("/insights/repetitive-patterns")
      .then((r) => setSuggestions(r.data.suggestions || []))
      .finally(() => setLoading(false));
  }, []);

  const apply = async (s) => {
    setApplying(s.workflow_id);
    try {
      await api.post("/insights/apply-suggestion", {
        workflow_id: s.workflow_id,
        suggested_cron: s.suggested_cron,
      });
      toast.success(`✅ Workflow programmé : ${s.workflow_name}`);
      setDismissed((d) => [...d, s.workflow_id]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setApplying(null);
    }
  };

  const visible = suggestions.filter((s) => !dismissed.includes(s.workflow_id));

  if (loading || visible.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-amber-50 border border-amber-200 rounded-sm p-6 mb-8"
      data-testid="insights-widget"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-sm bg-amber-600 text-white flex items-center justify-center">
          <Sparkles size={14}/>
        </div>
        <h2 className="font-display text-lg font-medium tracking-tight">Suggestions intelligentes</h2>
        <span className="text-[10px] font-mono text-amber-700 px-2 py-0.5 bg-amber-100 rounded-sm">{visible.length} IDÉE{visible.length > 1 ? "S" : ""}</span>
      </div>

      <AnimatePresence>
        {visible.map((s) => (
          <motion.div
            key={s.workflow_id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="bg-white border border-amber-200 rounded-sm p-4 mb-2 last:mb-0"
            data-testid={`suggestion-${s.workflow_id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm mb-1">{s.workflow_name}</p>
                <p className="text-xs text-zinc-600 mb-2">{s.pattern}</p>
                <p className="text-xs text-zinc-500 font-mono">
                  Suggestion : programmer chaque <strong>{s.day_label}</strong> à <strong>{s.hour}h</strong>
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  onClick={() => apply(s)}
                  disabled={applying === s.workflow_id}
                  className="rounded-sm bg-amber-600 text-white hover:bg-amber-700 h-8 text-xs"
                  data-testid={`apply-suggestion-${s.workflow_id}`}
                >
                  {applying === s.workflow_id ? "..." : (<><Zap size={12} className="mr-1"/> Automatiser</>)}
                </Button>
                <button
                  onClick={() => setDismissed((d) => [...d, s.workflow_id])}
                  className="w-8 h-8 rounded-sm border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center"
                  data-testid={`dismiss-suggestion-${s.workflow_id}`}
                >
                  <X size={12}/>
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
}
